import React, { useState } from 'react';

import { Confirm, DynamicIconWrapper, Icons, Modal, Tooltip } from '@components/common';
import Table from '@components/common/Table';
import CellForm from '@components/ModalComponents/CellForm';

import { useManageCellsBySites } from '@hooks/useManageCellsBySites';

import { ICell4GDTO, ICell5GDTO } from '@interfaces/cells/dto/cellsDTO';
import { TTechnologyNameTitle } from '@interfaces/common/common';

import { ETechnologiesValues, TTechnologiesValues } from '@enums/common';

import { MODAL_WIDTH_480, MODAL_WIDTH_580 } from '@constants/modals';
import { addTooltipText } from '../constants';

import { getColumns } from './columns';

import styles from './CellsTab.module.scss';

interface ICellTabProps {
	technology?: TTechnologyNameTitle & { node: string };
	setEditableTechnology: (technology: TTechnologyNameTitle & { node: string } | null) => void;
	deleteTechnology: (technologyValue: TTechnologiesValues) => void;
	id: string;
	isEditAccess: boolean;
	nodeName?: string;
}

const CellsTab = ({
	technology,
	setEditableTechnology,
	deleteTechnology,
	id,
	isEditAccess,
	nodeName,
}: ICellTabProps) => {
	const [isDeleteTechnologyConfirm, setIsDeleteTechnologyConfirm] = useState(false);
	const [isDeleteCellConfirm, setIsDeleteCellConfirm] = useState(false);
	const [isCellFormModal, setIsCellFormModal] = useState(false);
	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);
	const [singleIdForRemove, setSingleIdForRemove] = useState<string | null>(null);
	const [checkedCellsIds, setCheckedCellsIds] = useState<Array<string>>([]);

	const [editableCell, setEditableCell] = useState<Record<string, any> | null>(null);

	const openDeleteCellConfirm = () => setIsDeleteCellConfirm(prevState => !prevState);

	const closeDeleteConfirm = () => {
		setIsDeleteCellConfirm(false);
		setSingleIdForRemove(null);
	};

	const toggleDeleteTechnologyConfirm = () => technology
		&& isEditAccess && setIsDeleteTechnologyConfirm(prevState => !prevState);

	const onEditTechnologyClickHandler = () => technology
		&& !isDisabledEditTechnology && setEditableTechnology(technology);

	const toggleCellModal = () => setIsCellFormModal(prevState => !prevState);

	const openAddCellModal = () => {
		setEditableCell(null);
		toggleCellModal();
	};

	const closeCellModal = () => setIsCellFormModal(false);

	const {
		createCell4GTddHandler,
		createCell4GFddHandler,
		createCell5GHandler,
		isLoadingForm,
		cellsBySite,
		cellsCount,
		isLoadingCellTable,
		handleDeleteCell,
		handleDeleteCells,
		updateCell5GHandler,
		updateCell4GFddHandler,
		updateCell4GTddHandler,
	} = useManageCellsBySites({
		closeCellModal,
		id,
		technology,
		closeDeleteConfirm,
	});

	const isDisabledEditTechnology = !isEditAccess || !!cellsCount;

	const handleDeleteItems = () => {
		// TODO: Fix type of submitData
		if (singleIdForRemove) return handleDeleteCell({ id, cellId: singleIdForRemove } as any);

		return handleDeleteCells(checkedCellsIds as any);
	};

	const submitHandler = ({ values, cellId }: { values: ICell5GDTO | ICell4GDTO; cellId?: number }) => {
		const createSubmitData = {
			id,
			data: values,
		};

		const updateSubmitData = {
			id,
			cellId,
			data: values,
		};

		//TODO: Fix types of submitData
		switch (technology?.name) {
			case ETechnologiesValues.T_4G_TDD:
				editableCell
					? updateCell4GTddHandler(updateSubmitData as any)
					: createCell4GTddHandler(createSubmitData as any);
				break;
			case ETechnologiesValues.T_4G_FDD:
				editableCell
					? updateCell4GFddHandler(updateSubmitData as any)
					: createCell4GFddHandler(createSubmitData as any);
				break;
			case ETechnologiesValues.T_5G:
				editableCell
					? updateCell5GHandler(updateSubmitData as any)
					: createCell5GHandler(createSubmitData as any);
				break;
			default:
				return;
		}
	};

	const columns = getColumns({
		technology,
		isEditAccess,
		toggleCellModal,
		setEditableCell,
		openDeleteCellConfirm,
		setSingleIdForRemove,
	});

	return technology ?
		(<div className={styles.cellTabContainer}>
				<Modal
					isOpen={isDeleteTechnologyConfirm}
					onClose={toggleDeleteTechnologyConfirm}
				>
					<Confirm
						onSubmit={() => {
							deleteTechnology(technology?.name);
							setIsDeleteTechnologyConfirm(false);
						}}
						question='Are you sure you want to delete this technology?'
					/>
				</Modal>

				<Modal
					isOpen={isDeleteCellConfirm}
					onClose={closeDeleteConfirm}
					title='Confirm action'
					width={MODAL_WIDTH_480}
				>
					<Confirm
						onSubmit={handleDeleteItems}
						question='Are you sure you want to delete the Cell(s)?'
					/>
				</Modal>

				<Modal
					isConfirmDialogNeededOnClose
					isDirtyForm={isDirtyFormModal}
					isOpen={isCellFormModal}
					onClose={closeCellModal}
					title={editableCell ? 'Edit cell' : 'Add cell'}
					width={MODAL_WIDTH_580}
				>
					<CellForm
						editableCell={editableCell}
						is4GFdd={technology.name === ETechnologiesValues.T_4G_FDD}
						is4GTdd={technology.name === ETechnologiesValues.T_4G_TDD}
						is5G={technology.name === ETechnologiesValues.T_5G}
						isLoadingForm={isLoadingForm}
						setIsDirtyFormModal={setIsDirtyFormModal}
						submitHandler={submitHandler}
						toggleCellModal={toggleCellModal}
					/>
				</Modal>

				<div className={styles.node}>
					<div className={styles.description}>
						{Icons.manageSiteDB()}
						<span>{nodeName}:</span>&nbsp;{technology.node}
					</div>
					<div className={styles.actions}>
						<Tooltip content='To edit parameter you need to remove all cells'>
							<div onClick={onEditTechnologyClickHandler}>
								<DynamicIconWrapper
									icon={Icons.editIcon}
									isDisabled={isDisabledEditTechnology}
								/>
							</div>
						</Tooltip>
						<Tooltip content='Deleting technology will also delete all included cells'>
							<div onClick={toggleDeleteTechnologyConfirm}>
								<DynamicIconWrapper
									icon={Icons.delete}
									isDisabled={!isEditAccess}
								/>
							</div>
						</Tooltip>
					</div>
				</div>
				<Table
					addTooltipText={addTooltipText}
					columns={columns}
					data={cellsBySite}
					isCreatable={isEditAccess}
					isLoading={isLoadingCellTable}
					isWithoutCheckbox={!isEditAccess}
					isWithoutDeleteAction={!isEditAccess}
					isWithoutFilter
					keysForCheckedIds={['cellId']}
					pagination={{
						countOfRecords: cellsCount,
						withoutPagination: true,
					}}
					setCheckedIds={setCheckedCellsIds}
					toggleAddModal={openAddCellModal}
					toggleDeleteModal={openDeleteCellConfirm}
				/>
			</div>
		) : null;
};

export default CellsTab;
